// Jeopardy quiz types - adapted from jeopardy-quiz-show project
// These types define the board structure and live game state

export interface JeopardyClueMedia {
    image?: string;
    audio?: string;
    video?: string;
}

export interface JeopardyClue {
    id: string;
    points: number;
    question: string;
    answer: string;
    difficulty: 'Easy' | 'Medium' | 'Hard';
    media?: JeopardyClueMedia;
}

export interface JeopardyCategory {
    id: string;
    name: string;
    clues: JeopardyClue[];
}

export interface JeopardyFinalRound {
    category: string;
    question: string;
    answer: string;
}

/** The full board configuration stored in quiz_settings.additional_config */
export interface JeopardyBoardConfig {
    columnsCount: number;
    rowsCount: number;
    hasDoubleJeopardy: boolean;
    /** IDs of clues designated as Daily Doubles (1 in single, up to 2 in double) */
    dailyDoubleClueIds?: string[];
    jeopardyCategories: JeopardyCategory[];
    doubleJeopardyCategories: JeopardyCategory[];
    finalJeopardy: JeopardyFinalRound;
}

export interface JeopardyTeam {
    id: string;
    name: string;
    score: number;
    color: string;
    avatar: string;
    wager?: number;
    finalAnswer?: string;
    isCorrect?: boolean | null;
}

export type JeopardyPhase =
    | 'lobby'
    | 'single_jeopardy'
    | 'double_jeopardy'
    | 'final_jeopardy_wager'
    | 'final_jeopardy_question'
    | 'final_jeopardy_evaluation'
    | 'tie_breaker'
    | 'game_over';

/** Live game state stored in session_progress.additional_info */
export interface JeopardyGameState {
    phase: JeopardyPhase;
    teams: JeopardyTeam[];
    playedClues: Record<string, boolean>;
    activeClueId: string | null;
    activeClueType: 'single' | 'double' | null;
    clueRevealed: boolean;
    answeredByTeam: Record<string, 'correct' | 'incorrect'>;
    wagers: Record<string, number>;
    finalAnswers: Record<string, string>;
    finalEvaluations: Record<string, boolean | null>;
    wagerStepTeamIdx: number;
    evalStepTeamIdx: number;
    remainingSeconds: number;
    /** Daily Double: ID of active daily-double clue waiting for wager */
    pendingDailyDoubleClueId: string | null;
    pendingDailyDoubleTeamId: string | null;
    pendingDailyDoubleWager: number | null;
}

/** Settings stored in quiz_session.quiz_settings for a Jeopardy session */
export interface JeopardySessionSettings {
    teams: JeopardyTeam[];
    timerSeconds: number;
}

export const DEFAULT_JEOPARDY_GAME_STATE: JeopardyGameState = {
    phase: 'lobby',
    teams: [],
    playedClues: {},
    activeClueId: null,
    activeClueType: null,
    clueRevealed: false,
    answeredByTeam: {},
    wagers: {},
    finalAnswers: {},
    finalEvaluations: {},
    wagerStepTeamIdx: 0,
    evalStepTeamIdx: 0,
    remainingSeconds: 15,
    pendingDailyDoubleClueId: null,
    pendingDailyDoubleTeamId: null,
    pendingDailyDoubleWager: null,
};

export const JEOPARDY_TEAM_COLORS = [
    { hex: '#ef4444', name: 'Ruby Scarlet' },
    { hex: '#f59e0b', name: 'Solar Amber' },
    { hex: '#10b981', name: 'Emerald Mint' },
    { hex: '#3b82f6', name: 'Ocean Cobalt' },
    { hex: '#8b5cf6', name: 'Royal Amethyst' },
    { hex: '#ec4899', name: 'Hot Magenta' },
    { hex: '#14b8a6', name: 'Teal Tide' },
    { hex: '#f97316', name: 'Burnt Orange' },
];

export const JEOPARDY_TEAM_AVATARS = [
    '🎓', '🦁', '🦊', '🦉', '🚀', '🧠', '⚡', '🏆', '⭐', '🦖', '🐼', '🍕',
    '🔥', '🎯', '🌊', '🦋', '🎸', '🧩', '🌟', '🦄', '🐺', '🦅', '🐙', '🎲',
    '🗡️', '🛡️', '🎭', '🌈', '💎', '🎪', '🏴‍☠️', '🤖', '👾', '🎺',
];

